import { PublicationPricesPerDaySchema } from "../../types";

export const ALL_WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type Weekday = (typeof ALL_WEEKDAYS)[number];

/**
 * Manages the availability of publication days across multiple publications.
 * Tracks which days are occupied for each publication and provides utilities
 * to determine available days when adding or modifying publication entries.
 */
export class PublicationDaysManager {
  private occupiedDays: Map<string, Set<string>>;

  constructor(publicationsPrices: PublicationPricesPerDaySchema[]) {
    this.occupiedDays = new Map();

    // Build a map of occupied days per publication
    publicationsPrices.forEach((pubPrice) => {
      const publication = pubPrice.publication;
      if (!this.occupiedDays.has(publication)) {
        this.occupiedDays.set(publication, new Set());
      }

      const occupiedSet = this.occupiedDays.get(publication)!;
      pubPrice.days_prices.forEach((dayPrice) => {
        if (dayPrice.weekday) {
          occupiedSet.add(dayPrice.weekday);
        }
      });
    });
  }

  /**
   * Returns the days that are currently occupied for a given publication.
   */
  getOccupiedDays(publication: string): string[] {
    const occupied = this.occupiedDays.get(publication);
    return occupied ? Array.from(occupied) : [];
  }

  /**
   * Returns the days that are NOT occupied for a given publication.
   * These are the days that can be added for this publication.
   */
  getAvailableDays(publication: string): string[] {
    const occupied = this.occupiedDays.get(publication) || new Set();
    return ALL_WEEKDAYS.filter((day) => !occupied.has(day));
  }

  /**
   * Checks if a publication has at least one available day.
   * Returns true if the publication can be added to the list.
   */
  hasAvailableDays(publication: string): boolean {
    const occupied = this.occupiedDays.get(publication);
    if (!occupied) {
      return true;
    }
    return occupied.size < ALL_WEEKDAYS.length;
  }

  /**
   * Checks if all publications have all days occupied.
   * Used to determine if the "Add" button should be disabled.
   */
  areAllPublicationsFull(allPublications: string[]): boolean {
    return allPublications.every((pub) => !this.hasAvailableDays(pub));
  }

  /**
   * Returns a list of publications that have at least one available day.
   */
  getPublicationsWithAvailableDays(allPublications: string[]): string[] {
    return allPublications.filter((pub) => this.hasAvailableDays(pub));
  }
}
